import { type DocumentNode, useMutation } from "@apollo/client";
import { Alert, Button, Card, Divider } from "@blueprintjs/core";
import { useToaster } from "@package/ui-components";
import {
  DeleteLocationDocument,
  type DeleteLocationMutation,
  type DeleteLocationMutationVariables,
} from "@package/ui-gql/index-data-access";
import { useState } from "react";
import { useDuplicateItem } from "./use-duplicate-item";

export type ItemGridToolbarProps = {
  title: string;
  createEnabled?: boolean;
  deleteEnabled?: boolean;
  deleteDocument?: DocumentNode;
  deleteRefetchQueries?: DocumentNode[];
  duplicateEnabled?: boolean;
  duplicateGetDocument?: DocumentNode;
  duplicateCreateDocument?: DocumentNode;
  duplicateRefetchQueries?: DocumentNode[];
  duplicateNameField?: string;
  editEnabled?: boolean;
  exportEnabled?: boolean;
  onCreateClicked?: () => void;
  onEditClicked?: (id?: string) => void;
  onSelectedItemIdChanged?: (id?: string) => void;
  selectedItemId?: string;
};

export function ItemGridToolbar({
  title,
  createEnabled,
  deleteEnabled,
  deleteDocument,
  deleteRefetchQueries,
  duplicateEnabled,
  duplicateGetDocument,
  duplicateCreateDocument,
  duplicateRefetchQueries,
  duplicateNameField = "name",
  editEnabled,
  exportEnabled,
  onCreateClicked,
  onEditClicked,
  onSelectedItemIdChanged,
  selectedItemId,
}: ItemGridToolbarProps) {
  const { toaster } = useToaster();
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState<boolean>(false);
  const [deleteItem] = useMutation<
    DeleteLocationMutation,
    DeleteLocationMutationVariables
  >(deleteDocument ?? DeleteLocationDocument);
  const { duplicateItem, isDuplicateInflight, isConfigured } = useDuplicateItem(
    {
      title,
      getDocument: duplicateGetDocument,
      createDocument: duplicateCreateDocument,
      nameField: duplicateNameField,
      refetchQueries: duplicateRefetchQueries,
      onDuplicated: onSelectedItemIdChanged,
    },
  );

  if (duplicateEnabled && !isConfigured) {
    console.error(
      `ItemGridToolbar: duplicateEnabled is true for "${title}" but duplicateGetDocument/duplicateCreateDocument were not provided. Duplicate action disabled.`,
    );
  }

  return (
    <Card compact elevation={1}>
      <div className="flex flex-row grow">
        <h4 className="m-0 content-center bp6-heading">{title}</h4>
        <div className="flex grow justify-end">
          {!!createEnabled && (
            <>
              <Button
                minimal
                intent="primary"
                text="Create"
                icon="add"
                onClick={() => onCreateClicked?.()}
              />
              <Divider />
            </>
          )}
          {!!editEnabled && (
            <Button
              minimal
              intent="warning"
              text="Edit"
              icon="edit"
              disabled={!selectedItemId}
              onClick={() => onEditClicked?.(selectedItemId)}
            />
          )}
          {!!duplicateEnabled && (
            <Button
              minimal
              intent="none"
              text="Duplicate"
              icon="duplicate"
              disabled={!selectedItemId || isDuplicateInflight || !isConfigured}
              onClick={() => duplicateItem(selectedItemId)}
            />
          )}
          <Alert
            cancelButtonText="Cancel"
            canEscapeKeyCancel
            canOutsideClickCancel
            confirmButtonText="Delete"
            icon="trash"
            intent="danger"
            isOpen={isDeleteAlertOpen}
            onCancel={() => setIsDeleteAlertOpen(false)}
            onConfirm={() => {
              const toastId = toaster?.show({
                icon: "trash",
                intent: "none",
                message: `Deleting ${title}`,
                timeout: 10000,
              });

              deleteItem({
                notifyOnNetworkStatusChange: true,
                refetchQueries: deleteRefetchQueries,
                variables: { id: selectedItemId ?? "" },
                onCompleted: () => {
                  onSelectedItemIdChanged?.(undefined);

                  if (toastId) {
                    toaster?.dismiss(toastId);
                  }
                  toaster?.show({
                    icon: "tick",
                    intent: "success",
                    message: `Deleted ${title}`,
                    timeout: 3000,
                  });
                },
              });

              setIsDeleteAlertOpen(false);
            }}
          >
            <p>Are you sure you want to delete this resource?</p>
          </Alert>
          {!!deleteEnabled && (
            <Button
              minimal
              intent="danger"
              text="Delete"
              icon="trash"
              disabled={!selectedItemId}
              onClick={() => setIsDeleteAlertOpen(true)}
            />
          )}
          {!!exportEnabled && (
            <>
              <Divider />
              <Button
                minimal
                intent="none"
                text="Export"
                icon="export"
                disabled={!exportEnabled}
              />
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
